import { useCallback, useEffect, useState } from 'react';
import { ChefHat } from 'lucide-react';
import type { PedidoCardapio, RestauranteCardapio, StatusPedido } from '../../types/cardapio';
import { LABEL_STATUS_PEDIDO } from '../../types/cardapio';
import { listarPedidos, salvarPedidos } from '../../utils/cardapio/persistencia';

export default function CardapioCozinha({ restaurante }: { restaurante: RestauranteCardapio }) {
  const [senha, setSenha] = useState('');
  const [autenticado, setAutenticado] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoCardapio[]>([]);

  const recarregar = useCallback(() => {
    setPedidos(
      listarPedidos(restaurante.slug).filter((p) =>
        ['enviado_cozinha', 'preparando', 'pronto'].includes(p.status)
      )
    );
  }, [restaurante.slug]);

  useEffect(() => {
    if (!autenticado) return;
    recarregar();
    const id = window.setInterval(recarregar, 4000);
    return () => window.clearInterval(id);
  }, [autenticado, recarregar]);

  const mudar = (id: string, status: StatusPedido) => {
    const lista = listarPedidos(restaurante.slug);
    const idx = lista.findIndex((p) => p.id === id);
    if (idx < 0) return;
    lista[idx] = { ...lista[idx], status, atualizadoEm: new Date().toISOString() };
    salvarPedidos(restaurante.slug, lista);
    recarregar();
  };

  if (!autenticado) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4">
        <form
          className="bg-neutral-800 rounded-2xl shadow-lg p-6 max-w-sm w-full border border-neutral-700 text-white"
          onSubmit={(e) => {
            e.preventDefault();
            if (senha === restaurante.senhaPainel) setAutenticado(true);
            else alert('Senha incorreta.');
          }}
        >
          <ChefHat className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <h1 className="text-center font-bold">Cozinha — {restaurante.nome}</h1>
          <input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Senha"
            className="mt-4 w-full border border-neutral-600 bg-neutral-900 rounded-lg px-3 py-2 text-sm text-white"
          />
          <button
            type="submit"
            className="mt-3 w-full py-2 rounded-lg bg-orange-600 text-white font-semibold text-sm"
          >
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900 text-white">
      <header className="px-4 py-3 border-b border-neutral-700 flex items-center gap-2">
        <ChefHat className="w-5 h-5 text-orange-400" />
        <h1 className="font-bold">Cozinha — {restaurante.nome}</h1>
      </header>
      <main className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pedidos.length === 0 ? (
          <p className="text-neutral-500 col-span-full text-center py-16">Fila vazia</p>
        ) : (
          pedidos.map((p) => (
            <div
              key={p.id}
              className={`rounded-xl border p-4 ${
                p.status === 'pronto'
                  ? 'border-emerald-500 bg-emerald-950'
                  : 'border-neutral-600 bg-neutral-800'
              }`}
            >
              <p className="text-2xl font-black">Mesa {p.mesa}</p>
              <p className="text-xs text-neutral-400">{LABEL_STATUS_PEDIDO[p.status]}</p>
              {p.tempoEstimadoMin != null && (
                <p className="text-sm text-orange-300 mt-1">~{p.tempoEstimadoMin} min</p>
              )}
              <ul className="mt-2 text-sm space-y-0.5">
                {p.itens.map((it, i) => (
                  <li key={i}>
                    <strong>{it.quantidade}×</strong> {it.nome}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-1 mt-3">
                {p.status === 'enviado_cozinha' && (
                  <Btn onClick={() => mudar(p.id, 'preparando')}>Preparar</Btn>
                )}
                {p.status === 'preparando' && (
                  <Btn onClick={() => mudar(p.id, 'pronto')}>Pronto</Btn>
                )}
                {p.status === 'pronto' && (
                  <Btn onClick={() => mudar(p.id, 'entregue')}>Entregue</Btn>
                )}
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}

function Btn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-bold"
    >
      {children}
    </button>
  );
}
